import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Card, CardContent, CardHeader,
  CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, Sparkles } from "lucide-react";
import { apiClient } from "@/services/apiClient";
import { API_URLS, IN_APP_ROUTES } from "@/constants/urls";
import { REGISTER_USER_EMAIL } from "@/constants";
import GoogleOauth from "@/components/auth/GoogleOauth";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";
import ScrollToTop from "@/components/ScrollToTop";
import { useCompset } from "@/contexts/CompsetContext";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { cleanUpAccount } = useAuth();
  const { setActiveCompset } = useCompset();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    passwordConfirm: "",
    // name: "Test1",
    // email: "test1@gmail.com",
    // password: "Prowen@123",
    // passwordConfirm: "Prowen@123",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.passwordConfirm) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setActiveCompset(null);
    cleanUpAccount();

    setIsLoading(true);

    try {
      const result = await apiClient.post(API_URLS.REGISTER, {
        email: formData.email,
        password: formData.password,
        password_confirm: formData.passwordConfirm,
        name: formData.name
      });

      if (result.status == 200) {
        localStorage.setItem(REGISTER_USER_EMAIL, formData.email)
        navigate(IN_APP_ROUTES.OTP_VERIFICATION)
      }

    } catch (error) {
      apiErrorHandler(error)
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">

      <ScrollToTop />
      {/* Left Side (Same as Login Page) */}
      <div className="hidden lg:flex lg:w-1/2 hero-gradient relative overflow-hidden">
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">

          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center font-bold text-xl text-primary-foreground border border-primary-foreground/20">
              P
            </div>
            <span className="font-semibold text-2xl text-primary-foreground">
              Prowen
            </span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
            Make Smarter<br />
            <span className="text-accent-light">Pricing Decisions</span>
          </h1>

          <p className="text-lg text-primary-foreground/80 mb-8 max-w-md">
            Real-time competitor tracking, review monitoring, and market trend analysis for modern hotel portfolios.
          </p>

          <div className="space-y-4">
            {[
              "Competitor Rate Tracking",
              "Review & Rankings Monitoring",
              "Real-Time Market Trends",
              "AI-Powered Insights",
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3 text-primary-foreground/80">
                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-accent" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-light/10 rounded-full blur-3xl" />
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">

          {/* Mobile Logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center font-bold text-primary-foreground">
              P
            </div>
            <span className="font-semibold text-xl text-foreground">
              Prowen
            </span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl">Create Account</CardTitle>
              <CardDescription>
                Start your free trial today
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label>Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create password"
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label>Confirm Password</Label>

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                    <Input
                      name="passwordConfirm"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.passwordConfirm}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      className="pl-10 pr-10"
                      required
                    />

                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or</span>
                  </div>
                </div>
              </form>

              <div className='mt-2'>
                <GoogleOauth />
              </div>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            © {new Date().getFullYear()} Prowen Technologies India Private Limited
          </p>
        </div>
      </div>
    </div>
  );
}